import React, { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import NeuralField, { bandOfHz, colourForHz, dominantHz } from "./NeuralField";
import { Container, Eyebrow, Display, Lead, useReveal } from "./ui";
import { colors, fonts, media } from "../../styles/theme";
import { steps, stepsNext, platforms } from "../../pages/landingContent";
import { REGIONS, CONVERGENCE } from "../future/brainRegions";

// "How it works": the hero's instrument again. A linen panel carries the same wave field the
// hero uses, with a large readout of the dominant frequency and band, so "stress becomes
// flow" is visible rather than asserted. Replaced the dark forest panel on 27 Sep 2026.

// How it works page (Josh, 30 Sep 2026): the home hero's brain replaces the wave lines, and
// walks from high stress (stage 1, 30 Hz, stress center on alert) to flow (stage 5, 4 Hz).
// Loaded on its own so the old home page, which shares this section, never pulls in 3D.
const TractMind = lazy(() => import("../future/TractMind"));
const BRAIN_GROUND = "#121a16";

const rgb = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;
const STEP_CALM = [0, 0.35, 0.65, 0.85, 1];

/* ---------- shared layout (as the live section) ---------- */

const HowWrap = styled.section`
  padding: 140px 0 80px;
  @media ${media.large} {
    padding: 180px 0 120px;
  }

  /* How it works page (Josh, 29 Sep 2026): the heading and the instrument fit the first screen. */
  &.fit {
    @media ${media.medium} {
      padding-top: 108px;
    }
    h2 {
      font-size: clamp(38px, 4.2vw, 60px);
    }
    .grid {
      margin-top: 28px;
    }
    @media ${media.medium} {
      .panel {
        aspect-ratio: auto;
        height: clamp(320px, calc(100svh - 330px), 620px);
      }
    }
  }
`;
const HowGrid = styled.div`
  display: grid;
  gap: 48px;
  margin-top: 64px;
  @media ${media.medium} {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
    gap: 72px;
    align-items: start;
  }
`;
const Sticky = styled.div`
  position: sticky;
  top: 96px;
  z-index: 3;
  @media (max-width: 899px) {
    top: 72px;
  }
`;
const StepList = styled.ol`
  list-style: none;
  display: grid;
  gap: 24px;
  @media ${media.medium} {
    gap: 0;
  }
`;
const Step = styled.li`
  padding: 28px 0;
  border-top: 1px solid ${colors.stone};
  opacity: ${(p) => (p.$active ? 1 : 0.38)};
  transition: opacity 0.5s ease;
  @media ${media.medium} {
    min-height: 46vh;
    padding: 36px 0;
  }
`;
const StepNum = styled.span`
  font-family: ${fonts.mono};
  font-size: 13px;
  letter-spacing: 0.1em;
  color: ${colors.copper};
`;
const StepTitle = styled.h3`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(40px, 4.6vw, 64px);
  line-height: 1;
  letter-spacing: -0.035em;
  color: ${colors.ink};
  margin: 14px 0 16px;
`;
const Next = styled.li`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 22px 24px;
  border-radius: 18px;
  border: 1px dashed ${colors.stone};
  font-family: ${fonts.body};
  font-size: 16px;
  line-height: 1.5;
  color: ${colors.bark};
  &::before {
    content: "↗";
    color: ${colors.copper};
    font-weight: 600;
  }
`;
const Platforms = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 28px;
  span {
    font-family: ${fonts.mono};
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.pebble};
    margin-right: 4px;
  }
  b {
    padding: 8px 16px;
    border-radius: 999px;
    border: 1px solid ${colors.stone};
    background: ${colors.linen};
    font-family: ${fonts.body};
    font-size: 15px;
    font-weight: 600;
    color: ${colors.ink};
  }
`;

/* ---------- the readout, in the hero's language ---------- */

const Readout = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;
const Hz = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(30px, 3.2vw, 44px);
  line-height: 1;
  letter-spacing: -0.02em;
  white-space: nowrap;
  transition: color 0.4s ease;
  i {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: currentColor;
    box-shadow: 0 0 14px currentColor;
  }
  small {
    font-family: ${fonts.mono};
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.pebble};
    margin-left: 6px;
  }
`;
const State = styled.span`
  font-family: ${fonts.mono};
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${colors.bark};
  white-space: nowrap;
`;
const Progress = styled.div`
  display: flex;
  gap: 6px;
  span {
    width: 26px;
    height: 3px;
    border-radius: 3px;
    background: ${colors.stone};
    transition: background 0.4s ease;
  }
  span.on {
    background: ${colors.copper};
  }
`;

// The active step is measured, not toggled: on every scroll frame, the last step whose top
// has passed the 60% line is the one in view. Symmetric in both directions and self-correcting,
// where per-step triggers could fall a stage behind on the way back up.
const useActiveStep = (scope) => {
  const [active, setActive] = useState(0);
  useLayoutEffect(() => {
    const root = scope.current;
    if (!root) return undefined;
    const els = Array.from(root.querySelectorAll("[data-step]"));
    let raf = 0;
    const measure = () => {
      raf = 0;
      const line = window.innerHeight * 0.6;
      let idx = 0;
      els.forEach((el, i) => {
        if (el.getBoundingClientRect().top <= line) idx = i;
      });
      setActive((prev) => (prev === idx ? prev : idx));
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [scope]);
  return active;
};

/* ---------- A: the instrument ---------- */

const PanelA = styled.div`
  position: relative;
  border-radius: 28px;
  overflow: hidden;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};
  aspect-ratio: 5 / 4;
  box-shadow: 0 50px 100px -60px rgba(30, 42, 34, 0.35);
  @media (max-width: 899px) {
    aspect-ratio: 4 / 3;
  }
  canvas {
    position: absolute;
    inset: 16% 0 26%;
    height: 58%;
  }
  .top {
    position: absolute;
    top: 22px;
    left: 24px;
    right: 24px;
    display: flex;
    justify-content: space-between;
    font-family: ${fonts.mono};
    font-size: 11px;
    letter-spacing: 0.14em;
    color: ${colors.pebble};
  }
  .bottom {
    position: absolute;
    left: 24px;
    right: 24px;
    bottom: 22px;
    display: grid;
    gap: 14px;
  }

  /* the brain version: the home hero's dark ground, the brain between the label and the readout */
  &.brain {
    background: ${BRAIN_GROUND};
    border-color: rgba(250, 247, 241, 0.08);
    box-shadow: 0 50px 100px -60px rgba(0, 0, 0, 0.7);
  }
  /* the brain fills the panel, its edges faded into the ground so no band shows */
  &.brain .brainbox {
    position: absolute;
    inset: 0;
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent),
      linear-gradient(180deg, transparent 4%, #000 20%, #000 78%, transparent 96%);
    -webkit-mask-composite: source-in;
    mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent),
      linear-gradient(180deg, transparent 4%, #000 20%, #000 78%, transparent 96%);
    mask-composite: intersect;
  }
  &.brain .brainbox canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
  &.brain .top,
  &.brain ${Hz} small {
    color: rgba(250, 247, 241, 0.55);
  }
  &.brain .top,
  &.brain .bottom {
    z-index: 2;
  }
  &.brain ${State} {
    color: rgba(250, 247, 241, 0.8);
  }
  &.brain ${Progress} span {
    background: rgba(250, 247, 241, 0.16);
  }
  &.brain ${Progress} span.on {
    background: ${colors.copperLight};
  }
  /* portrait tablets keep the 4:3 panel; phones get a square one so the brain has room, with
     the brain kept above the readout, which wraps to two lines there */
  @media (max-width: 899px) {
    &.brain .brainbox {
      inset: 24px 0 80px;
    }
  }
  @media (max-width: 599px) {
    &.brain {
      aspect-ratio: 1 / 1;
    }
    &.brain .brainbox {
      inset: 24px 0 92px;
    }
  }
`;

// The stage's calm and frequency, handed to the brain, which eases between them on its own.
// Stages 1 to 4 walk the home hero's brain from stress to calm. Stage 5, flow, is the convergence
// point from "What happens in the brain": every region lit, the copper wiring (Josh, 30 Sep 2026:
// activate and flow read almost the same, so flow becomes the convergence image).
const FLOW_STAGE = 4;
const StageBrain = ({ active }) => {
  const calmRef = useRef(STEP_CALM[0]);
  const hzRef = useRef(dominantHz(STEP_CALM[0]));
  const levels = useRef(REGIONS.map(() => 0));
  const converge = active === FLOW_STAGE;
  levels.current = REGIONS.map(() => (converge ? 1 : 0));
  // Phones: the brain has a shorter space above the readout, so the camera comes in closer.
  const [phone, setPhone] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 899px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 899px)");
    const on = () => setPhone(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  useEffect(() => {
    calmRef.current = converge ? CONVERGENCE.calm : STEP_CALM[active];
    hzRef.current = converge ? CONVERGENCE.hz : dominantHz(STEP_CALM[active]);
  }, [active, converge]);
  return (
    <div className="brainbox" aria-hidden="true">
      <Suspense fallback={null}>
        <TractMind
          backdrop={BRAIN_GROUND}
          calmRef={calmRef}
          hzRef={hzRef}
          regions={REGIONS}
          levels={levels}
          baseTone={converge ? CONVERGENCE.tone : undefined}
          fibreGain={converge ? CONVERGENCE.gain : 1}
          position={[0, phone ? 0 : 0.2, 0]}
          turn={1.2}
          distance={phone ? 4.7 : 6.2}
        />
      </Suspense>
    </div>
  );
};

const HowShell = ({ children, id, dark }) => (
  <>
    <div data-reveal>
      <Eyebrow $color={dark ? colors.sage : colors.ocean} $onDark={dark}>
        How it works
      </Eyebrow>
      <Display id={id} $onDark={dark} style={{ marginTop: 20, maxWidth: 900 }}>
        From overload to <em>flow</em>, in five stages.
      </Display>
      <Platforms>
        <span>{platforms.label}</span>
        {platforms.devices.map((d) => (
          <b key={d}>{d}</b>
        ))}
      </Platforms>
    </div>
    {children}
  </>
);

const Steps = ({ active, dark }) => (
  <StepList>
    {steps.map((s, i) => (
      <Step key={s.key} data-step $active={i === active}>
        <StepNum>0{i + 1}</StepNum>
        <StepTitle>{s.title}</StepTitle>
        <Lead $onDark={dark}>{s.body}</Lead>
      </Step>
    ))}
    <Next>{stepsNext}</Next>
  </StepList>
);

const ReadoutBlock = ({ active }) => {
  const hz = dominantHz(STEP_CALM[active]);
  const band = bandOfHz(hz);
  const tone = rgb(colourForHz(hz, "light"));
  return (
    <Readout aria-hidden="true">
      <Hz style={{ color: tone }}>
        <i />
        {Math.round(hz)} Hz
        <small>{band.name}</small>
      </Hz>
      <State>{steps[active].state}</State>
      <Progress>
        {steps.map((s, i) => (
          <span key={s.key} className={i <= active ? "on" : ""} />
        ))}
      </Progress>
    </Readout>
  );
};

/* ---------- dark version: the home hero's green (How it works page, Josh 29 Sep 2026) ---------- */

const DarkWrap = styled(HowWrap)`
  background: var(--deep, #121a16);
  color: ${colors.linen};

  ${Step} {
    border-top-color: rgba(250, 247, 241, 0.12);
  }
  ${StepTitle} {
    color: ${colors.linen};
  }
  ${StepNum} {
    color: ${colors.copperLight};
  }
  ${Next} {
    border-color: rgba(250, 247, 241, 0.22);
    color: rgba(250, 247, 241, 0.72);
  }
  ${Platforms} span {
    color: rgba(250, 247, 241, 0.55);
  }
  ${Platforms} b {
    background: rgba(250, 247, 241, 0.06);
    border-color: rgba(250, 247, 241, 0.18);
    color: ${colors.linen};
  }
  ${PanelA} {
    background: #1a241e;
    border-color: rgba(250, 247, 241, 0.1);
    box-shadow: 0 50px 100px -60px rgba(0, 0, 0, 0.8);
  }
  ${PanelA} .top,
  ${Hz} small {
    color: rgba(250, 247, 241, 0.55);
  }
  ${State} {
    color: rgba(250, 247, 241, 0.8);
  }
  ${Progress} span {
    background: rgba(250, 247, 241, 0.16);
  }
  ${Progress} span.on {
    background: ${colors.copperLight};
  }
`;

export const HowItWorks = ({ dark = false, fit = false, brain = false }) => {
  const scope = useRef(null);
  useReveal(scope);
  const active = useActiveStep(scope);
  const Wrap = dark ? DarkWrap : HowWrap;
  return (
    <Wrap id="how" ref={scope} className={fit ? "fit" : ""} aria-labelledby="how-title">
      <Container>
        <HowShell id="how-title" dark={dark}>
          <HowGrid className="grid">
            <Sticky>
              <PanelA className={brain ? "panel brain" : "panel"}>
                <div className="top">
                  <span>{steps[active].hud}</span>
                  <span>ILLUSTRATIVE</span>
                </div>
                {brain ? (
                  <StageBrain active={active} />
                ) : (
                  <NeuralField variant={dark ? "dark" : "light"} calm={STEP_CALM[active]} scan={active === 3} lines={9} />
                )}
                <div className="bottom">
                  <ReadoutBlock active={active} />
                </div>
              </PanelA>
            </Sticky>
            <Steps active={active} dark={dark} />
          </HowGrid>
        </HowShell>
      </Container>
    </Wrap>
  );
};
